import React, { useState, useEffect, useRef } from "react";
import api from "../../api";
import "./BalloonPopModal.css";

// Define image paths
const blueImage = "/Minigames/Ballons/Blue.png";
const greenImage = "/Minigames/Ballons/Green.png";
const pinkImage = "/Minigames/Ballons/Pink.png";
const redImage = "/Minigames/Ballons/Red.png";
const gastlyImage = "/Minigames/Ballons/Gastly.png";
const poisonDartImage = "/Minigames/Ballons/Poison_Dart.png";
const seviperStrikeImage = "/Minigames/Ballons/Seviper_Strike.png";
const coinImage = "/items/coin.png";
const ticketImage = "/items/Ticket.png";

const BalloonPopModal = ({ isOpen, toggle, playerPos, spawnText }) => {
  // Game state
  const [gameActive, setGameActive] = useState(false);
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(60);
  const [balloons, setBalloons] = useState([]);
  const [dartPosition, setDartPosition] = useState({ x: 0, y: 0 });
  const [dartFired, setDartFired] = useState(false);
  const [gameStats, setGameStats] = useState({
    playsToday: 0,
    maxPlays: 4,
    tickets: 0
  });
  const [showResults, setShowResults] = useState(false);
  const [resultsData, setResultsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Refs
  const gameAreaRef = useRef(null);
  const gameLoopRef = useRef(null);
  const timerRef = useRef(null);

  // NEW: refs to avoid stale closures in RAF loop
  const gameActiveRef = useRef(false);
  const timeLeftRef = useRef(timeLeft);
  const dartFiredRef = useRef(false);
  const balloonsRef = useRef([]);
  const scoreRef = useRef(score);

  useEffect(() => { gameActiveRef.current = gameActive; }, [gameActive]);
  useEffect(() => { timeLeftRef.current = timeLeft; }, [timeLeft]);
  useEffect(() => { dartFiredRef.current = dartFired; }, [dartFired]);
  useEffect(() => { balloonsRef.current = balloons; }, [balloons]);
  useEffect(() => { scoreRef.current = score; }, [score]);
  
  // Constants
  const GAME_WIDTH = 800;
  const GAME_HEIGHT = 500; // Reduced height to fit better in modal
  const BALLOON_TYPES = ["Blue", "Green", "Pink", "Red"];
  const BALLOON_WIDTH = 32; // Each frame is 32x32
  const BALLOON_HEIGHT = 32; // Each frame is 32x32
  const DART_WIDTH = 20;
  const DART_HEIGHT = 40;
  const BALLOON_SPEED_MIN = 1.2; // Faster minimum speed
  const BALLOON_SPEED_MAX = 4; // Faster maximum speed
  const DART_SPEED = 15; // Faster dart
  const GAME_COST = 25;
  
  // Map balloon types to images
  const BALLOON_IMAGES = {
    Blue: blueImage,
    Green: greenImage,
    Pink: pinkImage,
    Red: redImage,
    Gastly: gastlyImage
  };
  
  // Reset game state completely
  const resetGame = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (gameLoopRef.current) {
      cancelAnimationFrame(gameLoopRef.current);
      gameLoopRef.current = null;
    }
    setGameActive(false);
    setShowResults(false);
    setScore(0);
    setTimeLeft(60);
    setBalloons([]);
    setDartFired(false);
    setError(null);
  };

  // Load game stats on mount and clean up on close
  useEffect(() => {
    if (isOpen) {
      loadGameStats();
    } else {
      resetGame(); // Reset game when modal is closed
    }

    return () => {
      resetGame(); // Also reset on unmount
    };
  }, [isOpen]);
  
  // Load player's game stats
  const loadGameStats = async () => {
    try {
      setLoading(true);
      const response = await api.get("/v1/balloon-pop/stats", { withCredentials: true });
      setGameStats(response.data);
      setLoading(false);
    } catch (err) {
      console.error("Error loading balloon pop stats:", err);
      setError("Failed to load game stats. Please try again.");
      setLoading(false);
    }
  };
  
  // Start a new game
  const startGame = async () => {
    try {
      // Check if player has enough coins
      const response = await api.post("/v1/balloon-pop/start", { 
        cost: GAME_COST 
      }, { withCredentials: true });
      
      if (response.data.success) {
        setGameActive(true);
        setScore(0);
        setTimeLeft(60);
        setBalloons([]);
        setDartFired(false);
        setShowResults(false);
        
        // Start game timer
        timerRef.current = setInterval(() => {
          setTimeLeft(prev => {
            if (prev <= 1) {
              endGame();
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
        
        // Spawn initial balloons
        spawnInitialBalloons();
        
        // CHANGED: do NOT call gameLoop() directly here.
        // The loop will be started by the useEffect below once gameActive is true.
      } else {
        setError(response.data.message || "Failed to start game");
      }
    } catch (err) {
      console.error("Error starting balloon pop game:", err);
      setError("Failed to start game. Please try again.");
    }
  };
  
  // End the current game
  const endGame = async () => {
    // Clear timers and game loop
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    if (gameLoopRef.current) {
      cancelAnimationFrame(gameLoopRef.current);
    }
    
    setGameActive(false);
    
    try {
      // 1. Submit score to backend using scoreRef to avoid stale closure issues
      const currentScore = scoreRef.current;
      console.log('Submitting score to backend:', currentScore);
      const endResponse = await api.post("/v1/balloon-pop/end", {
        score: currentScore
      }, { withCredentials: true });
      
      if (endResponse.data.success) {
        // 2. Fetch the updated stats from the server
        const statsResponse = await api.get("/v1/balloon-pop/stats", { withCredentials: true });
        const newStats = statsResponse.data;

        // 3. Set the data for the results screen
        setResultsData({
          finalScore: currentScore,
          totalTickets: newStats.tickets,
          playsToday: newStats.playsToday,
          maxPlays: newStats.maxPlays
        });

        // Also update the main gameStats for the pre-game screen
        setGameStats(newStats);

        // 4. Now, show the results screen
        setShowResults(true);
        
        // Show floating text with tickets earned
        if (spawnText && playerPos) {
          spawnText(`+${currentScore} tickets from Balloon Pop!`, playerPos.x, playerPos.y - 32, "#ffd700", "🎟️");
        }
      } else {
        setError(endResponse.data.message || "Failed to submit score");
      }
    } catch (err) {
      console.error("Error ending balloon pop game:", err);
      setError("Failed to submit score. Please try again.");
    }
  };
  
  // NEW: start/stop RAF when gameActive flips
  useEffect(() => {
    if (!gameActive) return;

    const step = () => {
      // read from refs to avoid stale state
      if (!gameActiveRef.current) return;

      updateBalloons();

      if (dartFiredRef.current) {
        updateDart();
      }

      if (Math.random() < 0.1) {
        spawnBalloon();
      }

      gameLoopRef.current = requestAnimationFrame(step);
    };

    gameLoopRef.current = requestAnimationFrame(step);
    return () => {
      if (gameLoopRef.current) cancelAnimationFrame(gameLoopRef.current);
    };
  }, [gameActive]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Update all balloon positions
  const updateBalloons = () => {
    setBalloons(prevBalloons => {
      if (prevBalloons.length === 0) {
        return prevBalloons;
      }
      
      // CHANGED: use ref for timeLeft to avoid stale multiplier
      const timeMultiplier = 1 + (60 - timeLeftRef.current) / 60;
      
      // Animate balloon frames and move upward
      const updatedBalloons = prevBalloons.map((balloon, index) => {
        if (balloon.popping) {
          // Animate burst frames
          return {
            ...balloon,
            burstFrame: balloon.burstFrame + 1
          };
        } else {
          // Normal balloon movement and animation
          const newY = balloon.y - (balloon.speed * timeMultiplier);
          
          
          return {
            ...balloon,
            y: newY, // Move UP (negative y)
            // Keep frame at 0 for normal balloons (no animation)
            frame: 0
          };
        }
      });
      
      // Remove balloons that have gone off screen (top) but not popping ones
      const filteredBalloons = updatedBalloons.filter(balloon => 
        balloon.popping || balloon.y > -BALLOON_HEIGHT
      );
      
      // Removed debug logging for cleaner console
      
      return filteredBalloons;
    });
  };
  
  // Update dart position and check for collisions
  const updateDart = () => {
    setDartPosition(prev => {
      const newY = prev.y - DART_SPEED;
      
      // CHANGED: use balloonsRef to avoid stale balloons in the loop
      let hitBalloon = null;
      for (const balloon of balloonsRef.current) {
        if (checkCollision(prev.x, newY, DART_WIDTH, DART_HEIGHT, 
                          balloon.x, balloon.y, BALLOON_WIDTH, BALLOON_HEIGHT)) {
          hitBalloon = balloon;
          break;
        }
      }
      
      // Handle balloon pop if collision detected
      if (hitBalloon) {
        handleBalloonPop(hitBalloon);
        setDartFired(false);
        return { x: 0, y: 0 };
      }
      
      // Reset dart if it goes off screen
      if (newY < -DART_HEIGHT) {
        setDartFired(false);
        return { x: 0, y: 0 };
      }
      
      return { ...prev, y: newY };
    });
  };
  
  // Spawn initial balloons
  const spawnInitialBalloons = () => {
    const initialBalloons = [];
    for (let i = 0; i < 5; i++) {
      // Create balloons at different positions
      const balloon = createBalloon();
      // Override position only, keep the speed from createBalloon
      balloon.x = 100 + (i * 120); // Spread across width with more spacing
      balloon.y = GAME_HEIGHT - 100; // Start at bottom, clearly visible
      initialBalloons.push(balloon);
    }
    console.log('Spawned initial balloons with ALL properties:', initialBalloons);
    setBalloons(initialBalloons);
  };
  
  // Spawn a new balloon
  const spawnBalloon = () => {
    const newBalloon = createBalloon();
    console.log('Spawning new balloon:', newBalloon);
    setBalloons(prev => {
      const updated = [...prev, newBalloon];
      console.log('Total balloons:', updated.length);
      return updated;
    });
  };
  
  // Create a new balloon object
  const createBalloon = () => {
    // Determine if this should be a Gastly balloon (5% chance)
    const isGastly = Math.random() < 0.05;
    
    // Calculate a random position across the width of the game area
    // But ensure it's not too close to the edges
    const padding = 20;
    const xPos = padding + Math.random() * (GAME_WIDTH - BALLOON_WIDTH - (padding * 2));
    
    const balloon = {
      id: Math.random().toString(36).substr(2, 9),
      x: xPos,
      y: GAME_HEIGHT - 5, // Start just inside the bottom edge
      type: isGastly ? "Gastly" : BALLOON_TYPES[Math.floor(Math.random() * BALLOON_TYPES.length)],
      speed: BALLOON_SPEED_MIN + Math.random() * (BALLOON_SPEED_MAX - BALLOON_SPEED_MIN),
      frame: 0, // Start with first frame
      frameCount: 3, // 3 frames per balloon (96px / 32px = 3 frames)
      popping: false,
      burstFrame: 0 // For burst animation
    };
    
    console.log('Created balloon:', balloon);
    return balloon;
  };
  
  // Handle balloon pop
  const handleBalloonPop = (balloon) => {
    // Start burst animation
    setBalloons(prev => prev.map(b => 
      b.id === balloon.id 
        ? { ...b, popping: true, burstFrame: 0 }
        : b
    ));
    
    // Remove balloon after burst animation
    setTimeout(() => {
      setBalloons(prev => prev.filter(b => b.id !== balloon.id));
    }, 300); // 300ms burst animation
    
    // Update score
    if (balloon.type === "Gastly") {
      setScore(prev => Math.max(0, prev - 25));
      
      // Visual feedback for losing points
      if (spawnText && playerPos) {
        spawnText("-25 tickets", playerPos.x, playerPos.y - 32, "#ff6b6b");
      }
    } else {
      setScore(prev => prev + 1); // Gain 1 ticket for regular balloons
      
      // Visual feedback for gaining points
      if (spawnText && playerPos) {
        spawnText("+1 ticket", playerPos.x, playerPos.y - 32, "#48bb78");
      }
    }
  };
  
  // Fire a dart
  const fireDart = (e) => {
    if (!gameActive || dartFired) {
      console.log('Cannot fire dart - gameActive:', gameActive, 'dartFired:', dartFired);
      return;
    }
    
    console.log('Firing dart at click position');
    
    // Get click position relative to game area
    const gameAreaRect = gameAreaRef.current.getBoundingClientRect();
    
    // Handle both mouse and touch events
    let clientX, clientY;
    if (e.touches && e.touches.length > 0) {
      // Touch event
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      // Mouse event
      clientX = e.clientX;
      clientY = e.clientY;
    }
    
    const x = clientX - gameAreaRect.left;
    const y = clientY - gameAreaRect.top;
    
    console.log('Click position:', { clientX, clientY, gameAreaRect, x, y });
    
    // Position dart at click position, not at bottom
    setDartPosition({ 
      x: x - (DART_WIDTH / 2), 
      y: y - (DART_HEIGHT / 2) // Start dart at click position
    });
    setDartFired(true);
    
    // Play sound effect (if we had one)
    // const shootSound = new Audio('/sounds/dart.mp3');
    // shootSound.play();
  };
  
  // Check collision between two rectangles
  const checkCollision = (x1, y1, w1, h1, x2, y2, w2, h2) => {
    return x1 < x2 + w2 &&
           x1 + w1 > x2 &&
           y1 < y2 + h2 &&
           y1 + h1 > y2;
  };
  
  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };
  
  if (!isOpen) return null;

  if (loading) {
    return (
      <div 
        className="game-popup-overlay"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1050
        }}
      >
        <div style={{
          background: 'linear-gradient(to bottom, rgba(29, 34, 46, 0.95), rgba(41, 50, 65, 0.95))',
          color: '#fff',
          padding: '20px',
          borderRadius: '12px',
          backdropFilter: 'blur(10px)',
          border: '2px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          textAlign: 'center'
        }}>
          Loading...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div 
        className="game-popup-overlay"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1050
        }}
      >
        <div 
          onClick={(e) => e.stopPropagation()}
          style={{
            background: 'linear-gradient(to bottom, rgba(29, 34, 46, 0.95), rgba(41, 50, 65, 0.95))',
            color: '#fff',
            padding: '20px',
            borderRadius: '12px',
            backdropFilter: 'blur(10px)',
            border: '2px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
            textAlign: 'center'
          }}
        >
          <h3 style={{ margin: '0 0 15px 0' }}>Error: {error}</h3>
          <button 
            onClick={toggle}
            style={{
              background: 'rgba(255, 255, 255, 0.1)',
              color: '#fff',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '4px',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
            onMouseOut={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.1)'}
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="game-popup-overlay"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px 10px',
        boxSizing: 'border-box',
        zIndex: 1050
      }}
    >
      <div 
        className="balloon-pop-popup"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '95%',
          maxWidth: 900,
          maxHeight: '95vh',
          background: 'linear-gradient(to bottom, rgba(29, 34, 46, 0.95), rgba(41, 50, 65, 0.95))',
          color: '#fff',
          borderRadius: 12,
          backdropFilter: 'blur(10px)',
          border: '2px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          margin: '10px'
        }}
      >
        {/* Header */}
        <div style={{
          padding: '12px 16px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'rgba(0, 0, 0, 0.2)',
          flexWrap: 'wrap',
          gap: '8px',
          minHeight: '60px'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            flex: '1 1 auto',
            minWidth: '200px'
          }}>
            <h3 style={{ 
              margin: 0, 
              fontSize: '18px', 
              fontWeight: 'bold',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>🎯 Seviper Strike Balloon Pop</h3>
            
            {gameActive && (
              <div className="game-stats" style={{ marginLeft: '20px', display: 'flex', gap: '15px' }}>
                <div className="time-left" style={{ color: '#f56565' }}>Time: {formatTime(timeLeft)}</div>
                <div className="score" style={{ display: 'flex', alignItems: 'center', color: '#48bb78' }}>
                  <img src={ticketImage} alt="Ticket" style={{ height: '20px', marginRight: '5px' }} />
                  {score}
                </div>
              </div>
            )}
          </div>

          <button
            onClick={toggle}
            style={{
              background: 'rgba(255, 255, 255, 0.1)',
              border: 'none',
              color: '#fff',
              borderRadius: '4px',
              padding: '6px 12px',
              cursor: 'pointer',
              transition: 'all 0.2s',
              fontSize: '14px'
            }}
            onMouseOver={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
            onMouseOut={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.1)'}
          >
            Close
          </button>
        </div>

        {/* Content */}
        <div style={{
          padding: '16px',
          flex: 1,
          overflow: 'auto'
        }}>
          {error && (
            <div className="alert alert-danger">{error}</div>
          )}
          
          {loading ? (
            <div className="text-center my-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <>
            {!gameActive && !showResults && (
              <div style={{ textAlign: 'center' }}>
                <div style={{ 
                  background: 'rgba(255, 255, 255, 0.05)', 
                  padding: '16px', 
                  borderRadius: '8px', 
                  marginBottom: '16px',
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}>
                  <h2>Welcome to Seviper Strike Balloon Pop!</h2>
                  <p>Pop as many balloons as you can in 60 seconds!</p>
                  <p>
                    Each balloon is worth one <img src={ticketImage} alt="Ticket" style={{ height: '20px', verticalAlign: 'middle' }} />, 
                    but watch out for Gastly balloons - they'll cost you <img src={ticketImage} alt="Ticket" style={{ height: '20px', verticalAlign: 'middle' }} />25!
                  </p>
                  <p>
                    Cost to play: <img src={coinImage} alt="Coin" style={{ height: '20px', verticalAlign: 'middle' }} /> {GAME_COST}
                  </p>
                  <div className="game-stats-summary">
                    <p>You have played {gameStats.playsToday} of {gameStats.maxPlays} games today</p>
                    <p>Total tickets earned: <img src={ticketImage} alt="Ticket" style={{ height: '20px', verticalAlign: 'middle', marginRight: '5px' }} /> {gameStats.tickets}</p>
                  </div>
                  
                  {gameStats.playsToday < gameStats.maxPlays ? (
                    <button 
                      onClick={startGame}
                      style={{
                        background: 'rgba(76, 175, 80, 0.8)',
                        color: '#fff',
                        border: 'none',
                        padding: '12px 24px',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        marginTop: '15px'
                      }}
                      onMouseOver={(e) => {
                        e.target.style.background = 'rgba(76, 175, 80, 1)';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.background = 'rgba(76, 175, 80, 0.8)';
                      }}
                    >
                      Start Game (Cost: <img src={coinImage} alt="Coin" style={{ height: '16px', verticalAlign: 'middle' }} />{GAME_COST})
                    </button>
                  ) : (
                    <div style={{ 
                      background: 'rgba(255, 193, 7, 0.2)', 
                      color: '#ffc107', 
                      padding: '10px', 
                      borderRadius: '8px', 
                      marginTop: '15px',
                      border: '1px solid rgba(255, 193, 7, 0.3)'
                    }}>
                      You've reached your daily play limit. Come back tomorrow!
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {showResults && resultsData && (
              <div style={{ textAlign: 'center' }}>
                <h2>Game Over!</h2>
                <div style={{ marginBottom: '20px' }}>
                  <p style={{ fontSize: '18px', marginBottom: '10px' }}>
                    You scored: {resultsData.finalScore} <img src={ticketImage} alt="Ticket" style={{ height: '24px', marginLeft: '5px' }} />
                  </p>
                  <p style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', opacity: 0.8 }}>
                    Total tickets: {resultsData.totalTickets} <img src={ticketImage} alt="Ticket" style={{ height: '20px', marginLeft: '5px' }} />
                  </p>
                </div>
                
                {resultsData.playsToday < resultsData.maxPlays ? (
                  <button 
                    onClick={startGame}
                    style={{
                      background: 'rgba(76, 175, 80, 0.8)',
                      color: '#fff',
                      border: 'none',
                      padding: '12px 24px',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.background = 'rgba(76, 175, 80, 1)';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.background = 'rgba(76, 175, 80, 0.8)';
                    }}
                  >
                    Play Again (Cost: <img src={coinImage} alt="Coin" style={{ height: '16px', verticalAlign: 'middle' }} />{GAME_COST})
                  </button>
                ) : (
                  <div style={{ 
                    background: 'rgba(255, 193, 7, 0.2)', 
                    color: '#ffc107', 
                    padding: '10px', 
                    borderRadius: '8px',
                    border: '1px solid rgba(255, 193, 7, 0.3)'
                  }}>
                    You've reached your daily play limit. Come back tomorrow!
                  </div>
                )}
              </div>
            )}
            
            {gameActive && (
              <div 
                className="game-area" 
                ref={gameAreaRef}
                onClick={fireDart}
                onTouchStart={fireDart}
                style={{ 
                  width: GAME_WIDTH, 
                  height: GAME_HEIGHT,
                  backgroundImage: `url(${seviperStrikeImage})`,
                  backgroundSize: 'contain', // Show full image without cropping
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  position: 'relative',
                  margin: '0 auto',
                  backgroundColor: '#2a4a3a' // Fallback color for any empty space
                }}
              >
                {/* Render balloons */}
                {balloons.map(balloon => (
                  <div
                    key={balloon.id}
                    className={`balloon ${balloon.type.toLowerCase()} ${balloon.popping ? 'popping' : ''}`}
                    style={{
                      left: balloon.x,
                      top: balloon.y,
                      width: BALLOON_WIDTH,
                      height: BALLOON_HEIGHT,
                      backgroundImage: `url(${BALLOON_IMAGES[balloon.type]})`,
                      backgroundSize: '96px 32px', // Full sprite sheet size
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: balloon.popping 
                        ? `-${(balloon.burstFrame % 3) * 32}px 0px` // Burst animation
                        : '0px 0px', // Always show first frame for normal balloons
                      zIndex: 100,
                      position: 'absolute',
                      transform: balloon.popping ? 'scale(1.2)' : 'scale(1)',
                      opacity: balloon.popping ? 0.7 : 1,
                      transition: balloon.popping ? 'transform 0.3s, opacity 0.3s' : 'none'
                    }}
                  />
                ))}
                
                {/* Render dart */}
                {dartFired && (
                  <div
                    className="dart"
                    style={{
                      left: dartPosition.x,
                      top: dartPosition.y,
                      width: DART_WIDTH,
                      height: DART_HEIGHT,
                      backgroundImage: `url(${poisonDartImage})`,
                      backgroundSize: 'contain',
                      zIndex: 20
                    }}
                  />
                )}
                
                {/* Render dart shooter */}
                <div 
                  className="dart-shooter" 
                  style={{ backgroundImage: `url(${poisonDartImage})` }}
                />
              </div>
            )}
          </>
        )}
        </div>
      </div>
    </div>
  );
};

export default BalloonPopModal;
